import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useVersion } from '../context/VersionContext';

const dateFmt = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${dateFmt.format(d)} Uhr`;
};

/**
 * Verlauf der letzten Versionsschritte (die API liefert 20). Pro Eintrag die
 * Commit-Betreffs des jeweiligen Deploys — daher steht hier, was sich geändert hat.
 */
export function VersionHistoryModal({ onClose }: { onClose: () => void }) {
  const { info, running } = useVersion();
  const history = info?.history ?? [];

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Versionsverlauf"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Versionsverlauf</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Sie arbeiten mit <span className="font-medium">v{running}</span>
              {info?.version && info.version !== running && (
                <> · aktuell veröffentlicht: <span className="font-medium">v{info.version}</span></>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          {history.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Noch kein Verlauf vorhanden — er entsteht ab dem nächsten Deploy.
            </p>
          ) : (
            <ol className="flex flex-col gap-4">
              {history.map(entry => {
                const isRunning = entry.version === running;
                return (
                  <li
                    key={`${entry.version}-${entry.releasedAt}`}
                    className={`rounded-lg border p-3 ${
                      isRunning
                        ? 'border-blue-300 dark:border-blue-700 bg-blue-50/60 dark:bg-blue-900/20'
                        : 'border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                        v{entry.version}
                        {isRunning && (
                          <span className="ml-2 text-[12px] font-medium uppercase tracking-wide text-blue-600 dark:text-blue-300">
                            in Benutzung
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {formatDate(entry.releasedAt)}
                      </span>
                    </div>
                    {entry.changes.length === 0 ? (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                        Keine Änderungen erfasst (Deploy ohne neue Commits).
                      </p>
                    ) : (
                      <ul className="mt-2 space-y-1">
                        {entry.changes.map((c, i) => (
                          <li key={i} className="text-xs text-slate-600 dark:text-slate-300 flex gap-2">
                            <span className="text-slate-400 dark:text-slate-500">·</span>
                            <span className="flex-1">{c}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
